import { createContext, useContext, useMemo, useEffect, type ReactNode } from 'react'
import { useAuthStore } from '@/store/authStore'
import { useProfileStore, type ProfileMode } from '@/store/profileStore'
import { usePermissionContext } from '@/contexts/PermissionContext'
import type { UserProfile } from '@/types/auth'

/**
 * Profile types eligible for the "entity side" of the switcher.
 * ENTITY = entity profile, ORGANIZATION = company profile.
 */
const SWITCHABLE_ENTITY_TYPES = ['ENTITY', 'ORGANIZATION']

const BUSINESS_CLIENT_ALLOWED_PATHS = [
  '/dashboard',
  '/orders',
  '/create-order',
  '/account-settings',
  '/access-denied',
]

interface ProfileSwitchContextType {
  activeMode: ProfileMode
  isBusinessClientMode: boolean
  /**
   * True when the business-client order creation flow is active:
   * explicit business client mode, or a BC-only account (no entity/org profile).
   */
  canAccessBusinessClientOrderCreation: boolean
  canSwitchProfiles: boolean
  entityProfile: UserProfile | null
  businessClientProfile: UserProfile | null
  switchToEntity: () => void
  switchToBusinessClient: () => void
  toggleProfile: () => void
  isPathAllowedInBusinessClientMode: (path: string) => boolean
}

const ProfileSwitchContext = createContext<ProfileSwitchContextType | undefined>(undefined)

export function ProfileSwitchProvider({ children }: { children: ReactNode }) {
  const storeUser = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const { activeMode, setActiveMode, reset } = useProfileStore()

  // Read profiles from PermissionContext (fed by useCurrentUser GraphQL)
  // so the switcher reacts immediately when profiles arrive after login
  // — without requiring a page refresh.
  const { profiles: contextProfiles, isLoadingPermissions } = usePermissionContext()

  const profiles = useMemo(() => {
    const storeProfiles = storeUser?.profiles ?? []
    // Prefer context profiles (from GraphQL) as they contain ALL profile types;
    // fall back to store profiles (from REST, may be channel-filtered).
    return contextProfiles.length > 0 ? contextProfiles : storeProfiles
  }, [contextProfiles, storeUser?.profiles])

  const entityProfile = useMemo(
    () => profiles.find((p) => SWITCHABLE_ENTITY_TYPES.includes(p.profile_type)) ?? null,
    [profiles]
  )

  const businessClientProfile = useMemo(
    () => profiles.find((p) => p.profile_type === 'BUSINESS_CLIENT') ?? null,
    [profiles]
  )

  const canSwitchProfiles = profiles.length > 1

  const canAccessBusinessClientOrderCreation = useMemo(() => {
    if (activeMode === 'business_client') return true
    const hasEntity = profiles.some((p) =>
      SWITCHABLE_ENTITY_TYPES.includes(p.profile_type)
    )
    const hasBC = profiles.some((p) => p.profile_type === 'BUSINESS_CLIENT')
    return !hasEntity && hasBC
  }, [activeMode, profiles])

  useEffect(() => {
    if (!isAuthenticated) {
      reset()
      return
    }
    // Keep localStorage mode (e.g. business_client) until profiles are loaded.
    // Otherwise refresh briefly has zero profiles and incorrectly resets to entity.
    if (isLoadingPermissions) return
    if (profiles.length === 0) return

    if (activeMode === 'business_client' && !businessClientProfile) {
      setActiveMode('entity')
    }
    if (activeMode === 'entity' && !entityProfile) {
      if (businessClientProfile) {
        setActiveMode('business_client')
      }
    }
  }, [
    isAuthenticated,
    isLoadingPermissions,
    profiles.length,
    entityProfile,
    businessClientProfile,
    activeMode,
    setActiveMode,
    reset,
  ])

  const switchToEntity = () => setActiveMode('entity')
  const switchToBusinessClient = () => setActiveMode('business_client')
  const toggleProfile = () =>
    setActiveMode(activeMode === 'entity' ? 'business_client' : 'entity')

  const isPathAllowedInBusinessClientMode = (path: string): boolean => {
    if (activeMode !== 'business_client') return true
    return BUSINESS_CLIENT_ALLOWED_PATHS.some(
      (allowed) => path === allowed || path.startsWith(allowed + '/')
    )
  }

  const value = useMemo<ProfileSwitchContextType>(
    () => ({
      activeMode,
      isBusinessClientMode: activeMode === 'business_client',
      canAccessBusinessClientOrderCreation,
      canSwitchProfiles,
      entityProfile,
      businessClientProfile,
      switchToEntity,
      switchToBusinessClient,
      toggleProfile,
      isPathAllowedInBusinessClientMode,
    }),
    [
      activeMode,
      canAccessBusinessClientOrderCreation,
      canSwitchProfiles,
      entityProfile,
      businessClientProfile,
    ]
  )

  return (
    <ProfileSwitchContext.Provider value={value}>
      {children}
    </ProfileSwitchContext.Provider>
  )
}

export function useProfileSwitch() {
  const ctx = useContext(ProfileSwitchContext)
  if (!ctx) {
    throw new Error('useProfileSwitch must be used within a ProfileSwitchProvider')
  }
  return ctx
}
